import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Toggle } from '@/components/ui'
import type { QuoteStatus } from '../api/types'

/**
 * Mirrors the CofA Management filter-rail shape exactly: collapsible
 * left rail with a Status chip group + Customer/Submitter contains
 * inputs + Options toggles. Same DOM idioms, same Tailwind classes,
 * same Section/ChipGroup helper components.
 */

export type QuoteStatusFilter = QuoteStatus | 'all'

export interface QuotesClientFilters {
  status: QuoteStatusFilter
  customerQuery: string
  submitterQuery: string
  includeArchived: boolean
}

// eslint-disable-next-line react-refresh/only-export-components -- defaults colocated with the rail that owns them; cheap, no consumer churn.
export const DEFAULT_FILTERS: QuotesClientFilters = {
  status: 'all',
  customerQuery: '',
  submitterQuery: '',
  includeArchived: false,
}

const STATUS_OPTIONS: { value: QuoteStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'in_review', label: 'Pricing In Review' },
  { value: 'revise_pricing', label: 'Revise Pricing' },
  { value: 'approved', label: 'Pricing Approved' },
  { value: 'sent', label: 'Sent' },
  { value: 'customer_approved', label: 'Customer Approved' },
  { value: 'customer_rejected', label: 'Customer Rejected' },
]

function countActiveFilters(f: QuotesClientFilters): number {
  let n = 0
  if (f.status !== 'all') n += 1
  if (f.customerQuery.trim()) n += 1
  if (f.submitterQuery.trim()) n += 1
  if (f.includeArchived) n += 1
  return n
}

export interface QuotesFilterRailProps {
  filters: QuotesClientFilters
  onChange: (next: QuotesClientFilters) => void
  collapsed: boolean
  onToggleCollapsed: () => void
}

export function QuotesFilterRail({
  filters, onChange, collapsed, onToggleCollapsed,
}: QuotesFilterRailProps) {
  const activeCount = countActiveFilters(filters)

  if (collapsed) {
    return (
      <aside
        className="flex w-8 flex-col items-center border-r border-border-subtle bg-surface-sunk"
        aria-label="Filters (collapsed)"
      >
        <button
          type="button"
          aria-label="Expand filters"
          onClick={onToggleCollapsed}
          className="mt-2 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronRight size={14} />
        </button>
        {activeCount > 0 ? (
          <span className="text-role-badge-count mt-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-subtle px-1.5 text-accent-text">
            {activeCount}
          </span>
        ) : null}
      </aside>
    )
  }

  return (
    <aside
      aria-label="Filters"
      className="flex w-[var(--layout-filter-rail-width)] flex-col gap-4 overflow-y-auto border-r border-border-subtle bg-surface-sunk p-3"
    >
      <header className="flex items-center justify-between border-b border-border-subtle pb-2">
        <span className="text-role-section-label">
          Filters
          {activeCount > 0 ? (
            <span className="ml-1.5 text-accent-text">· {activeCount}</span>
          ) : null}
        </span>
        <button
          type="button"
          aria-label="Collapse filters"
          onClick={onToggleCollapsed}
          className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        >
          <ChevronLeft size={14} />
        </button>
      </header>

      <Section label="Status">
        <ChipGroup
          options={STATUS_OPTIONS}
          value={filters.status}
          onChange={(v) => onChange({ ...filters, status: v as QuoteStatusFilter })}
        />
      </Section>

      <Section label="Customer contains">
        <input
          type="text"
          value={filters.customerQuery}
          onChange={(e) => onChange({ ...filters, customerQuery: e.target.value })}
          placeholder="Acme Corp or 10002"
          className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
        />
      </Section>

      <Section label="Submitted by">
        <input
          type="text"
          value={filters.submitterQuery}
          onChange={(e) => onChange({ ...filters, submitterQuery: e.target.value })}
          placeholder="user@spi.com"
          className="w-full rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
        />
      </Section>

      <Section label="Options">
        <div className="flex flex-col gap-2">
          <Toggle
            checked={filters.includeArchived}
            onChange={(e) => onChange({ ...filters, includeArchived: e.target.checked })}
            label="Include archived"
          />
        </div>
      </Section>
    </aside>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <header className="text-role-section-label">{label}</header>
      {children}
    </section>
  )
}

function ChipGroup<T extends string>({
  options, value, onChange,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className="inline-flex items-center gap-1 rounded-xs border border-border-default bg-surface px-2.5 py-1 text-xs text-primary hover:bg-surface-hover aria-pressed:border-accent aria-pressed:bg-accent-subtle aria-pressed:text-accent-text"
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
